// React hook that bridges TanStack Query cache to CallToneController.
//
// Subscribes once per mount to queryCache.subscribe, derives transitions
// from per-phone cache updates, and feeds them to the controller. Single
// `currentTrack` (one active call at a time — UI blocks phone switching
// during a call). On unmount: unsubscribe + cancelAll to stop any
// lingering ringback.

import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  CallToneController,
  getDefaultController,
  type CallDirection,
  type CallState,
  type TrackState,
} from '../lib/callTones'
import type { Phone, ActiveCall } from '../types'

interface InternalTrack extends TrackState {
  phoneId: string
}

function inferDirection(
  state: ActiveCall['state'],
  prev: InternalTrack | null,
  callId: string,
): CallDirection {
  if (prev && prev.callId === callId) return prev.direction
  if (state === 'ringing') return 'incoming'
  return 'outgoing'
}

function trackFromPhone(
  phone: Phone | undefined,
  phoneId: string,
  prev: InternalTrack | null,
): InternalTrack | null {
  const cur = phone?.currentCall
  if (!cur) return null
  // 'holding' is in the union but never produced by relay-cache. Coerce to
  // 'active' as a defensive default so the controller never sees a state
  // it can't classify.
  const state: CallState =
    cur.state === 'dialing' || cur.state === 'ringing' ? cur.state : 'active'
  return {
    phoneId,
    callId: cur.callId,
    state,
    direction: inferDirection(state, prev, cur.callId),
  }
}

function isSameTrack(a: InternalTrack | null, b: InternalTrack | null): boolean {
  if (a === null && b === null) return true
  if (a === null || b === null) return false
  return a.callId === b.callId && a.state === b.state && a.direction === b.direction
}

export function useCallToneSubscriber(controller?: CallToneController): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    const ctrl = controller ?? getDefaultController()
    let currentTrack: InternalTrack | null = null

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      // 'added' / 'updated' = setQueryData firings. Other types
      // (removed = cache GC, observerAdded/Removed/ResultsUpdated =
      // React-cycle noise) don't reflect content transitions and would
      // flood the controller with no-ops. Dedupe-by-content below also
      // catches these, but filtering here avoids the work.
      if (event.type !== 'added' && event.type !== 'updated') return

      const qk: unknown = event.query.queryKey
      if (
        !Array.isArray(qk) ||
        qk.length < 2 ||
        qk[0] !== 'phones' ||
        typeof qk[1] !== 'string'
      ) {
        return
      }

      const phoneId = qk[1]
      const phone = event.query.state.data as Phone | undefined
      const next = trackFromPhone(phone, phoneId, currentTrack)

      // Event for a different phone with no currentCall (status flip on
      // an idle phone) — irrelevant to the active track.
      if (currentTrack !== null && currentTrack.phoneId !== phoneId && next === null) {
        return
      }

      if (isSameTrack(currentTrack, next)) return

      ctrl.onTransition({
        prev: currentTrack
          ? {
              callId: currentTrack.callId,
              state: currentTrack.state,
              direction: currentTrack.direction,
            }
          : null,
        next: next
          ? { callId: next.callId, state: next.state, direction: next.direction }
          : null,
      })
      currentTrack = next
    })

    return () => {
      unsubscribe()
      ctrl.cancelAll()
    }
  }, [queryClient, controller])
}
